import React, { useState, useEffect, useRef } from 'react';
import { Grid2 } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import moment from 'moment';
import ExpenseDatePicker from './ExpenseDatePicker';
import { ExpenseStatusValuesEnum } from './ExpenseStatus.enum';
import { COLORS } from '../../../utils/Application_Constants';
import { ExpenseModeEnum } from './ExpenseMode.enum';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export const toPayDateKey = (value) => {
    if (value == null || value === '') return null;
    if (typeof value === 'string') {
        const datePart = value.substring(0, 10);
        return DATE_ONLY.test(datePart) ? datePart : null;
    }
    const parsed = moment.isMoment(value) ? value.clone() : moment(value);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

const parsePayDate = (value) => {
    const key = toPayDateKey(value);
    return key ? moment(key, 'YYYY-MM-DD') : null;
};

const ExpensePayDate = (props) => {
    const {
        index,
        expense,
        mode,
        isPosted,
        isApprovedOrInvoiced,
        isPendingSubmission,
        isViewOnly,
        enableEditByCorrection = false,
    } = props;
    const { control, watch, errors, setValue, getValues } = useFormContext();
    const minDate = moment(new Date()).startOf('day');
    const maxDate = moment(new Date()).startOf('day').add(90, 'days');
    const billType = watch(`expenseEntries[${index}].billType`);
    const isClinicianDeduction = billType === 'ClinicianDeduction';
    // Pay Date stays editable in Invoice Review (AE2-6506 AC)
    const disabled = isViewOnly || (isPosted && isViewOnly) || isPendingSubmission;
    const [required, setRequired] = useState({ required: true });
    const originalPayDateRef = useRef(
        expense?.payDate || getValues?.(`expenseEntries[${index}].payDate`)
    );
    const handleDateChange = (e) => {
        if (e && e.startOf('day')) {
            setValue(`expenseEntries[${index}].payDate`, e.startOf('day').format('YYYY-MM-DD'));
        } else {
            setValue(`expenseEntries[${index}].payDate`, e);
        }
    };
    useEffect(() => {
        setRequired({
            required:
                expense?.status !== ExpenseStatusValuesEnum.NonReimbursable &&
                expense?.status !== ExpenseStatusValuesEnum.Abandoned &&
                (mode !== ExpenseModeEnum.CreditCard ||
                    (mode === ExpenseModeEnum.CreditCard &&
                        isClinicianDeduction &&
                        (expense?.status === ExpenseStatusValuesEnum.Approved ||
                            expense?.status === ExpenseStatusValuesEnum.InvoiceReview))),
        });
    }, [expense?.status, isClinicianDeduction, mode]);

    useEffect(() => {
        if (!originalPayDateRef.current) {
            originalPayDateRef.current =
                expense?.payDate || getValues?.(`expenseEntries[${index}].payDate`);
        }
    }, [expense?.payDate, getValues, index]);

    const isUnchangedPayDate = (value) => {
        const originalKey = toPayDateKey(originalPayDateRef.current || expense?.payDate);
        const valueKey = toPayDateKey(value);
        return Boolean(originalKey && valueKey && originalKey === valueKey);
    };

    const validationRules =
        enableEditByCorrection || !(isPosted || isApprovedOrInvoiced || isViewOnly)
            ? {
                  required: required.required
                      ? 'Pay Date is required. Please enter a valid date.'
                      : false,
                  validate: {
                      invalid: (value) => !value || moment(value).isValid() || 'Invalid date',
                      maxDate: (value) => {
                          const parsed = parsePayDate(value);
                          if (!parsed) return true;
                          if (isUnchangedPayDate(value)) return true;
                          return parsed.isSameOrBefore(maxDate) || 'Max date reached';
                      },
                      minDate: (value) => {
                          const parsed = parsePayDate(value);
                          if (!parsed) return true;
                          if (isUnchangedPayDate(value)) return true;
                          return parsed.isSameOrAfter(minDate) || 'Min date reached';
                      },
                  },
              }
            : {};

    return (
        <Grid2 item size={{ xs: 12, md: 6 }}>
            <Controller
                control={control}
                name={`expenseEntries[${index}].payDate`}
                defaultValue={expense.payDate || null}
                rules={validationRules}
                render={({ onChange, value }) => (
                    <ExpenseDatePicker
                        onChange={(e) => {
                            onChange(e);
                            handleDateChange(e);
                        }}
                        inputProps={{ disableUnderline: true }}
                        value={value ? moment(value) : null}
                        id={`pay-date${index}`}
                        label="Pay date"
                        {...required}
                        disabled={disabled}
                        minDate={
                            isUnchangedPayDate(watch(`expenseEntries[${index}].payDate`))
                                ? undefined
                                : minDate
                        }
                        maxDate={
                            isUnchangedPayDate(watch(`expenseEntries[${index}].payDate`))
                                ? undefined
                                : maxDate
                        }
                        parentTextFieldProps={{
                            error: Boolean(errors.expenseEntries?.[index]?.payDate),
                            helperText: errors.expenseEntries?.[index]?.payDate?.message,
                        }}
                        parentSx={{
                            '& .MuiInputBase-root': {
                                borderColor: !errors.expenseEntries?.[index]?.payDate
                                    ? 'default'
                                    : `${COLORS.LT_PEPPER} !important`,
                            },
                        }}
                    />
                )}
            />
        </Grid2>
    );
};

export default ExpensePayDate;
